import React from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';

import { AlertInfo, Close } from '@rover/icons';
import { Box, Flex, Heading, Link, Paragraph, Text } from '@rover/kibble/core';
import { Spacing } from '@rover/kibble/styles';
import { useTheme } from '@rover/kibble/themes';
import { onSpaceOrEnterKeyPress } from '@rover/react-lib/src/utils/input';
import { NotificationsAnnouncements } from '@rover/rsdk/src/apiClient/latest';
import getDateTimeFormatMapForLang from '@rover/shared/js/constants/i18n/datetime';

export type Props = {
  notification: NotificationsAnnouncements;
  language?: string;
  onClick?: (opk: string) => void;
  onDismiss?: (opk: string) => void;
};

const OUTER_COLUMN_WIDTH = 24;
const ICON_COLUMN_WIDTH = 24;
const DISMISS_COLUMN_WIDTH = 24;

const NotificationsCenterRow = ({
  notification,
  language,
  onClick,
  onDismiss,
  ...other
}: Props): JSX.Element | null => {
  const { background, colors } = useTheme();
  if (notification.isDismissed) return null;
  const pinned = !notification.isDismissible;
  if (language) {
    moment.locale(language);
  }

  return (
    <Flex
      {...other}
      as="li"
      flex="1 0"
      flexWrap="wrap"
      order={pinned ? 0 : 1}
      padding="4x"
      paddingRight="2x"
      borderBottom="1px solid"
      borderColor={colors.border.primary.toString()}
      data-testid="notifications-center-row"
      sx={{
        background: pinned ? background.secondary.toString() : background.primary.toString(),
        '&:hover': {
          background: pinned ? background.tertiary.toString() : background.secondary.toString(),
        },
      }}
    >
      <Flex flex="1" flexWrap="wrap" tabIndex={0}>
        {!pinned && notification.createdAt && (
          <Flex
            flex="1 1 100%"
            sx={{ paddingLeft: `${Spacing.S.toNumber() + ICON_COLUMN_WIDTH}px` }}
            pb="1x"
          >
            <Text size="100" fontWeight="semibold" textColor="tertiary">
              {language &&
                moment(notification.createdAt).format(
                  getDateTimeFormatMapForLang(language).MONTH_DAY_FULL
                )}
            </Text>
          </Flex>
        )}

        <Flex data-testid="alert-info-icon" flex={`0 0 ${ICON_COLUMN_WIDTH}px`}>
          <AlertInfo
            aria-label={t`Alert icon`}
            height={OUTER_COLUMN_WIDTH}
            width={OUTER_COLUMN_WIDTH}
          />
        </Flex>

        <Flex
          flex="1"
          flexDirection="column"
          paddingLeft="2x"
          onClick={() => {
            if (onClick) onClick(notification.opk);
          }}
          data-testid="message-column"
          sx={{
            ...(!notification.webAction ? { cursor: 'pointer' } : {}),
            a: {
              textDecoration: 'underline',
            },
          }}
        >
          {notification.title && (
            <Heading
              size="200"
              fontWeight="semibold"
              textColor="secondary"
              marginBottom="1x"
              as="div"
            >
              {notification.title}
            </Heading>
          )}

          {(notification.html || notification.text) && (
            <>
              <Paragraph size="100" textColor="tertiary" mb="0x">
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: notification.html || notification.text }}
                />
              </Paragraph>
              {notification.webAction && (
                <Paragraph mb="0x">
                  <Link size="100" href={notification.webAction}>
                    {notification.actionText}
                  </Link>
                </Paragraph>
              )}
            </>
          )}
        </Flex>
      </Flex>
      <Flex flex={`0 0 ${DISMISS_COLUMN_WIDTH}px`} pr="8x">
        {!pinned && (
          <Box
            onClick={() => {
              if (onDismiss) onDismiss(notification.opk);
            }}
            onKeyDown={(e) => {
              if (onDismiss) onSpaceOrEnterKeyPress(e, onDismiss.bind(null, notification.opk));
            }}
            tabIndex={0}
            aria-label={t`Dismiss notification`}
            data-testid="dismiss-button-icon"
            sx={{
              cursor: 'pointer',
              color: colors.text.tertiary.toString(),

              '&:hover': {
                color: colors.text.secondary.toString(),
              },
            }}
          >
            <Close
              aria-label={t`Dismiss notification`}
              height={OUTER_COLUMN_WIDTH}
              width={OUTER_COLUMN_WIDTH}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export default NotificationsCenterRow;
